import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Users, Phone, IndianRupee, TrendingUp, PhoneIncoming, PhoneOutgoing, Activity, Clock, Loader2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border shadow-none p-4" data-testid={`admin-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}1a` }}><Icon className="w-4 h-4" style={{ color }} /></div>
      </div>
      <div className="text-2xl font-bold font-display mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    adminApi.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    adminApi.get("/admin/health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "#7C3AED" }} /></div>;
  const k = data.kpis;
  const trend = Array.from({ length: 30 }, (_, i) => ({ d: i + 1, calls: Math.round(20 + 30 * Math.sin(i / 4) + i) }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display">Platform Overview</h1>
        <p className="text-sm text-slate-500">Everything across all your clients at a glance.</p>
      </div>

      {health && (
        <Card className="border shadow-none p-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="admin-health-bar">
          <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1.5"><Activity className="w-4 h-4" /> All Systems Operational</span>
          {health.services.map((s) => (
            <span key={s.name} className="text-xs text-slate-500 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {s.name} <b className="text-slate-400">{s.uptime}%</b></span>
          ))}
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label="Active Clients" value={k.active_clients} sub={`${k.pending_clients} pending approval`} color="#7C3AED" />
        <Kpi icon={Phone} label="Total Calls" value={k.total_calls} sub="across all clients" color="#06B6D4" />
        <Kpi icon={IndianRupee} label="Revenue MTD" value={fmtINR(k.revenue_mtd_inr)} sub={`${k.active_subscriptions} subscriptions`} color="#10B981" />
        <Kpi icon={TrendingUp} label="Total Leads" value={k.total_leads} sub="in system" color="#F97316" />
        <Kpi icon={PhoneIncoming} label="Inbound Calls" value={k.inbound} color="#06B6D4" />
        <Kpi icon={PhoneOutgoing} label="Outbound Calls" value={k.outbound} color="#7C3AED" />
        <Kpi icon={Users} label="Total Clients" value={k.total_clients} color="#64748B" />
        <Kpi icon={Clock} label="Uptime" value={`${k.uptime_pct}%`} sub="last 30 days" color="#10B981" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border shadow-none p-5 lg:col-span-2">
          <h3 className="font-semibold font-display text-sm mb-4">Platform Call Volume (30 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend}><defs><linearGradient id="v" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7C3AED" stopOpacity={0.4} /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} /><XAxis dataKey="d" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Area dataKey="calls" stroke="#7C3AED" strokeWidth={2} fill="url(#v)" /></AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card className="border shadow-none">
          <div className="p-5 border-b"><h3 className="font-semibold font-display text-sm">Recent Platform Events</h3></div>
          <div className="divide-y max-h-[240px] overflow-y-auto">
            {data.recent_events.length === 0 ? <div className="p-6 text-sm text-slate-400 text-center">No events yet.</div> :
              data.recent_events.map((e) => (
                <div key={e.id} className="px-5 py-2.5 text-xs" data-testid="admin-event">
                  <div className="font-medium capitalize">{(e.action || "").replace(/_/g, " ")}</div>
                  <div className="text-slate-400">{e.admin_email} · {(e.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
          </div>
        </Card>
      </div>

      <Card className="border shadow-none">
        <div className="p-5 border-b flex items-center justify-between"><h3 className="font-semibold font-display text-sm">Top Clients by Usage</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-5 py-2">Client</th><th className="px-5 py-2">Plan</th><th className="px-5 py-2">Calls</th><th className="px-5 py-2">Leads</th><th className="px-5 py-2">Spend</th><th className="px-5 py-2">Status</th></tr></thead>
            <tbody>
              {data.top_clients.map((c) => (
                <tr key={c.tenant_id} className="border-b hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer" onClick={() => nav(`/admin/clients/${c.tenant_id}`)} data-testid={`admin-top-client-${c.tenant_id}`}>
                  <td className="px-5 py-2.5 font-medium">{c.company_name}</td>
                  <td className="px-5 py-2.5">{c.plan}</td>
                  <td className="px-5 py-2.5">{c.calls}</td>
                  <td className="px-5 py-2.5">{c.leads}</td>
                  <td className="px-5 py-2.5">{fmtINR(c.spend)}</td>
                  <td className="px-5 py-2.5"><StatusBadge status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    trial: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
    suspended: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
    churned: "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${map[status] || map.churned}`}>{status}</span>;
}
